import { forwardRef, useImperativeHandle, useState } from "react";
import { Select } from "antd";
import { notification } from "antd";
import { getUniqueElements, partitionByFunction } from "@/shared/utils/array/array.utils";

import style from "./style.module.css";

type EmailChipsProps = {
  disabled?: boolean;
  onChange?: (emails: string[]) => void;
  value?: string[];
  placeholder?: string;
};

export type EmailChipsInputRef = {
  hasUnhandledDanglingEmailText: () => boolean;
};

const EMAIL_SEPARATORS = [",", ";", " ", "\n", "\t"];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i; // not fully RFC 5322

const EmailChipsInput = forwardRef<EmailChipsInputRef, EmailChipsProps>(
  (
    { disabled = false, onChange = (_) => {}, value = [], placeholder = "Add recipients" }: EmailChipsProps,
    ref: React.Ref<EmailChipsInputRef>,
  ) => {
    const [internalSearch, setInternalSearch] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);

    useImperativeHandle(ref, () => ({
      hasUnhandledDanglingEmailText: () => {
        return internalSearch.length !== 0;
      },
    }));

    const handleChange = (vals: string[]) => {
      if (vals.length - value.length > 1) {
        // Many new values means we got a paste. Reject non-email values.
        handleChangeMany(vals);
      } else {
        // One new value means we got a single value, check its validity only proceed
        // if it's valid.
        handleChangeOne(vals);
      }
      setIsDeleting(false);
    };

    const handleChangeMany = (vals: string[]) => {
      const trimmedVals = vals.map((val) => val.trim());
      const deduplicatedVals = getUniqueElements(trimmedVals);

      const [valids, invalids] = partitionByFunction(deduplicatedVals, (val) => EMAIL_REGEX.test(val));

      if (invalids.length > 0) {
        const invalidAddresses = `"` + deduplicatedVals.filter((val) => !EMAIL_REGEX.test(val)).join(`", "`) + `"`;

        notification.error({
          message: `The address(es) ${invalidAddresses} in the "To" field were not recognized. Please make sure that all addresses are properly formed.`,
        });
      }

      onChange(valids);
    };

    const handleChangeOne = (vals: string[]) => {
      if (vals.length > 0 && !EMAIL_REGEX.test(vals[vals.length - 1])) {
        // Ignore delimiter-triggered changes if we don't have a valid email.
        setInternalSearch(vals[vals.length - 1] || "");
      } else {
        onChange(vals);
        setInternalSearch("");
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Tab is a special case to prevent moving focus to the next input.
      if (internalSearch && internalSearch.length > 0 && e.key === "Tab") {
        e.preventDefault();
        const newValue = value.concat(internalSearch);
        handleChange(newValue);
      }

      if (e.key === "Backspace") {
        // When text-selecting the entire search input and attempting to delete it,
        // we set this ahead of handleSearch to allow internalSearch to end up
        // an empty string.
        setIsDeleting(true);
      }
    };

    const handleSearch = (value: string) => {
      // In practice we want internalSearch to always track what we get here,
      // however, in delimiter-triggered `change` cases, we get an empty string here,
      // and want to let `setInternalSearch` in the `handleChange` cases take care of it.

      // Avoids some race conditions.

      if (value.length > 0 || isDeleting) {
        setInternalSearch(value);
        setIsDeleting(false);
      }
    };

    return (
      <Select
        mode="tags"
        className={style.emailChipsInput}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        open={false}
        placeholder={placeholder}
        tokenSeparators={EMAIL_SEPARATORS}
        searchValue={internalSearch}
        onSearch={handleSearch}
        autoFocus={true}
        disabled={disabled}
      />
    );
  },
);

EmailChipsInput.displayName = "EmailChipsInput";

export default EmailChipsInput;
